import { useCallback, useEffect, useRef, useState } from "react";
import EmailEditorCore, { EmailEditorCoreRef } from "../../shared/email-editor-core";
import { createPortal } from "react-dom";
import { PenLine, X, Maximize2, Minimize2 } from "lucide-react";
import { PlainTextEditor } from "../../shared/plain-text-editor";

type TabMode = "plain" | "editor";

export const DraftDrawer: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  initialHtml?: string;
  initialSubject?: string;
  initialPreviewText?: string;
  onSave: (data: {
    html: string;
    subject: string;
    previewText: string;
    plainText: string;
  }) => void;
}> = ({
  isOpen,
  onClose,
  initialHtml = "",
  initialSubject = "",
  initialPreviewText = "",
  onSave,
}) => {
  const [activeTab, setActiveTab] = useState<TabMode>("plain");
  const [subject, setSubject] = useState(initialSubject);
  const [previewText, setPreviewText] = useState(initialPreviewText);
  const [htmlText, setHtmlText] = useState(initialHtml);
  const [isExpanded, setIsExpanded] = useState(false);

  const editorRef = useRef<EmailEditorCoreRef | null>(null);
  const [, setIsEditorReady] = useState(false);

  // Only sync state when the drawer actually opens (not on every prop change)
  const prevOpenRef = useRef(false);
  useEffect(() => {
    if (isOpen && !prevOpenRef.current) {
      setSubject(initialSubject);
      setPreviewText(initialPreviewText);
      setHtmlText(initialHtml);
      setIsEditorReady(false);
      setActiveTab("plain");
      setIsExpanded(false); // Reset expanded state when drawer opens
    }
    prevOpenRef.current = isOpen;
  }, [isOpen, initialSubject, initialPreviewText, initialHtml]);

  // Prevent body scroll while drawer is open
  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [isOpen]);

  const handleEditorReady = useCallback(() => {
    setIsEditorReady(true);
    if (initialHtml) {
      setTimeout(() => {
        editorRef.current?.loadDesign({
          counters: { u_column: 1, u_row: 1, u_content_html: 1 },
          body: {
            rows: [{
              cells: [1],
              columns: [{
                contents: [{
                  type: "html",
                  values: { html: initialHtml, containerPadding: "20px" },
                }],
                values: { backgroundColor: "", padding: "0px" },
              }],
              values: { padding: "0px" },
            }],
            values: { backgroundColor: "#ffffff", contentWidth: "600px" },
          },
          schemaVersion: 6,
        });
      }, 300);
    }
  }, [initialHtml]);

  const handleTemplateGenerated = useCallback(
    (data: { html: string; subject?: string; header?: string; plainText?: string }) => {
      setHtmlText(data.html);
      if (data.subject) setSubject(data.subject);
      if (data.header) setPreviewText(data.header);
    },
    []
  );

  const extractPlainText = (html: string): string => {
    if (!html) return "";
    const div = document.createElement("div");
    div.innerHTML = html;
    return (div.textContent || div.innerText || "").replace(/\s+/g, " ").trim();
  };

  const handleSave = () => {
    onSave({ html: htmlText, subject, previewText, plainText: extractPlainText(htmlText) });
    onClose();
  };

  const isValid = subject.trim().length > 0 && htmlText.trim().length > 0;

  // Don't render anything into the DOM at all until first open
  const [everOpened, setEverOpened] = useState(false);
  useEffect(() => { if (isOpen) setEverOpened(true); }, [isOpen]);
  if (!everOpened) return null;

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/40 z-[9998] transition-opacity duration-300 ${isOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
          }`}
        onClick={onClose}
      />

      {/* Drawer — dynamically change width based on expanded state */}
      <div
        className={`fixed top-0 right-0 h-screen bg-white dark:bg-gray-900 shadow-2xl z-[9999] flex flex-col transition-all duration-300 ease-in-out ${isOpen ? "translate-x-0" : "translate-x-full"
          } ${isExpanded ? "w-[85%]" : "w-[560px]"}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Header ── */}
        <div className="px-6 py-5 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
              <PenLine size={16} className="text-gray-600 dark:text-gray-300" />
            </div>
            <h2 className="text-base font-bold text-gray-900 dark:text-gray-100">Draft New</h2>
          </div>

          <div className="flex gap-2">
            {/* Expand/Collapse Button */}
            <button
              type="button"
              onClick={() => setIsExpanded(!isExpanded)}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-[#7B7B7B] font-semibold dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              title={isExpanded ? "Collapse" : "Expand"}
            >
              {isExpanded ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
            </button>

            {/* Close Button */}
            <button
              type="button"
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-[#7B7B7B] font-semibold dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* ── Scrollable body ── */}
        <div className="flex-1 overflow-y-auto flex flex-col px-6 py-5 gap-5">
          {/* Tab switcher */}
          <div className="flex rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700 bg-[#f0f0f8] dark:bg-gray-800 p-1 gap-1 shrink-0">
            {(["plain", "editor"] as TabMode[]).map((tab) => (
              <button
                key={tab}
                type="button"
                onClick={() => setActiveTab(tab)}
                className={`flex-1 py-2.5 text-sm font-semibold rounded-lg transition-all ${activeTab === tab
                  ? "bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm"
                  : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                  }`}
              >
                {tab === "plain" ? "Plain Text" : "Email Editor"}
              </button>
            ))}
          </div>

          {/* Subject */}
          <div className="flex items-start gap-4 shrink-0">
            <div className="w-28 shrink-0 pt-2.5">
              <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Subject</span>
              <div className={`text-xs mt-0.5 ${subject.length > 70 ? "text-red-500" : "text-[#7B7B7B] font-semibold dark:text-gray-500"}`}>
                {subject.length}/70
              </div>
            </div>
            <input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Enter a subject"
              className="flex-1 px-3 py-2.5 border border-gray-200 dark:border-gray-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-transparent"
            />
          </div>

          {/* Preview Text */}
          <div className="flex items-start gap-4 shrink-0">
            <div className="w-28 shrink-0 pt-2.5">
              <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Preview Text</span>
              <div className={`text-xs mt-0.5 ${previewText.length > 140 ? "text-red-500" : "text-[#7B7B7B] font-semibold dark:text-gray-500"}`}>
                {previewText.length}/70
              </div>
            </div>
            <input
              value={previewText}
              onChange={(e) => setPreviewText(e.target.value)}
              placeholder="Enter a preview text"
              className="flex-1 px-3 py-2.5 border border-gray-200 dark:border-gray-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-transparent"
            />
          </div>

          {/* Editor area */}
          <div className="flex-1 flex flex-col min-h-0">
            {activeTab === "plain" && (
              <PlainTextEditor value={htmlText} onChange={setHtmlText} />
            )}
            {activeTab === "editor" && (
              <div
                className="flex-1 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden"
                style={{ minHeight: isExpanded ? "calc(100vh - 200px)" : "500px" }}
              >
                <EmailEditorCore
                  ref={editorRef}
                  minHeight={isExpanded ? "calc(100vh - 200px)" : "500px"}
                  onReady={handleEditorReady}
                  onTemplateGenerated={handleTemplateGenerated}
                />
              </div>
            )}
          </div>
        </div>

        {/* ── Footer ── */}
        <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-800 flex flex-col gap-2 shrink-0">
          <button
            type="button"
            onClick={handleSave}
            disabled={!isValid}
            className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-xl disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
          >
            Save and Close
          </button>
          <button
            type="button"
            onClick={onClose}
            className="w-full py-3 border border-gray-200 dark:border-gray-700 rounded-xl text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
          >
            Discard Changes
          </button>
        </div>
      </div>
    </>,
    document.body
  );
};